import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import ControlledModal from "../Modal/ControlledModal";
import { Button } from "../ui/button";
import {
  useBulkImportLibraryMutation,
  useLazyGetLibraryImportQuery,
} from "../../reactWrapper/Redux/apiSlice";
import { TOKEN } from "../../helper/constant";
import { useTypedSelector } from "../../reactWrapper/Redux/utilities";
import { LIBRARY, KINDS } from "./library.config";

const API = process.env.REACT_APP_BASE_API_URL;

/* Sheet columns in order, and which library kind each one maps to. `list`
   columns may hold several values separated by commas; the server splits
   them and reports one verdict per value. `description` has no kind of its
   own: it belongs to the category beside it, and its verdict shows there. */
const COLUMNS = [
  { key: "category", label: "category", kind: "category" },
  { key: "description", label: "description", kind: null },
  { key: "role", label: "role", kind: "role" },
  { key: "specialty", label: "specialty", kind: "specialty" },
  { key: "tool", label: "tool", kind: "tool", list: true },
  { key: "skill", label: "skill", kind: "skill", list: true },
  { key: "aliases", label: "aliases", kind: null },
];

/** The one-line verdict under a value, or null when there is nothing to say. */
function Verdict({ v, value, chosen }) {
  if (!v) return null;
  if (v.action === "create") {
    return (
      <div className="mt-1 text-xs text-emerald-700">
        new{v.name !== value ? ` as "${v.name}"` : ""}
        {v.generic ? " · generic" : ""}
        {v.category ? ` → ${v.category}` : ""}
      </div>
    );
  }
  if (v.action === "assign")
    return (
      <div className="mt-1 text-xs text-blue-700">assign → {v.category}</div>
    );
  if (v.action === "update")
    return <div className="mt-1 text-xs text-blue-700">update {v.field}</div>;
  if (v.action === "generic")
    return (
      <div className="mt-1 text-xs text-blue-700">
        {v.shared_with?.length
          ? `both ${v.shared_with.join(
              " and ",
            )} claim this skill — making it generic`
          : "mark as generic"}
      </div>
    );
  if (v.action === "link")
    return (
      <div className="mt-1 text-xs text-muted-foreground">
        link → {v.role || v.specialty}
      </div>
    );
  if (v.action === "reuse" && chosen)
    return (
      <div className="mt-1 text-xs text-blue-700">
        using existing "{v.name}"
      </div>
    );
  if (v.action === "reuse" && v.generic)
    return (
      <div className="mt-1 text-xs text-muted-foreground">existing generic</div>
    );
  return null;
}

/**
 * Bulk upload. Picking a file runs the preview straight away (a dry run on
 * the server). The preview is the sheet itself as a table; under each cell
 * the server's verdict for that value: new, link, assign, a suggestion of a
 * similar existing item, or an error. Comma-separated tool and skill cells
 * render one chip per value, each with its own verdict. Import applies
 * exactly the preview, then closes: the summary that used to sit behind a
 * "Done" button said nothing the preview had not already shown, and the
 * toast reports what was written.
 */
export default function BulkUploadModal({ open, onClose }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [resolutions, setResolutions] = useState({}); // "row:kind" or "row:kind:index" -> existing id
  const [bulk, { isLoading }] = useBulkImportLibraryMutation();
  const [fetchJob] = useLazyGetLibraryImportQuery();
  /* The running import, once one has been started. The work no longer
     happens inside the request, so this is the only way to know. */
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);
  const accessToken = useTypedSelector((s) => s.auth?.accessToken);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setPreview(null);
    setError("");
    setOnlyProblems(false);
    setResolutions({});
    setJob(null);
    stopPolling();
    if (inputRef.current) inputRef.current.value = "";
  }, [open]);

  /* The interval outlives a re-render but must not outlive the component. */
  useEffect(() => stopPolling, []);

  const describe = (err, importing = false) => {
    if (
      err?.status === "FETCH_ERROR" ||
      /failed to fetch/i.test(err?.error || "")
    ) {
      return importing
        ? "The connection was lost during import. Refresh the Role Library to check whether it completed before retrying."
        : "Could not reach the server to check this sheet. Check your connection and try again.";
    }
    return (
      err?.data?.error?.message ||
      err?.data?.message ||
      err?.error ||
      "Something went wrong. Please try again."
    );
  };

  const runPreview = async (f, res) => {
    setError("");
    try {
      const out = await bulk({
        file: f,
        dryRun: true,
        resolutions: res,
      }).unwrap();
      setPreview(out.data);
    } catch (err) {
      setPreview(null);
      setError(describe(err));
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setPreview(null);
    setResolutions({});
    if (f) runPreview(f, {});
  };

  const toggleResolution = (key, id) => {
    const next = { ...resolutions };
    if (next[key]) delete next[key];
    else next[key] = id;
    setResolutions(next);
    runPreview(file, next);
  };

  /* Stop polling. Called on success, on failure, and when the modal closes,
     so a forgotten interval can never outlive the dialog. */
  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  /* Follow an import that is already running -- the one we just started, or
     the one the server told us about when it refused a second. */
  const follow = (importId) => {
    stopPolling();
    const tick = async () => {
      try {
        const out = await fetchJob(importId).unwrap();
        const j = out.data;
        setJob(j);
        if (j.status === "done") {
          stopPolling();
          const created = Object.values(j.summary?.create || {}).reduce(
            (a, b) => a + b,
            0,
          );
          toast.success(
            `Imported: ${created} created, ${j.summary?.reuse ?? 0} reused, ${j.summary?.link ?? 0} linked`,
          );
          onClose?.({ imported: true });
        } else if (j.status === "failed") {
          stopPolling();
          setError(j.error || "The import failed.");
        }
      } catch (err) {
        /* A dropped poll is not a failed import -- the work is running on the
           server either way. Keep trying; the next tick usually lands. */
      }
    };
    tick();
    pollRef.current = setInterval(tick, 2000);
  };

  const doImport = async () => {
    setError("");
    try {
      const out = await bulk({ file, dryRun: false, resolutions }).unwrap();
      /* 202: the id of a job that is now running on the server. */
      setJob({
        id: out.data.import_id,
        status: out.data.status || "queued",
        total_rows: out.data.total_rows,
        written_rows: 0,
      });
      follow(out.data.import_id);
    } catch (err) {
      /* Refused because one is already running: follow that one rather than
         leaving the admin to guess. */
      const busyId = err?.data?.error?.import_id;
      if (err?.data?.error?.code === "IMPORT_BUSY" && busyId) {
        setError(err.data.error.message || "An import is already running.");
        setJob({ id: busyId, status: "running" });
        follow(busyId);
        return;
      }
      setError(describe(err, true));
    }
  };

  const downloadTemplate = async () => {
    try {
      const token = accessToken || localStorage.getItem(TOKEN) || "";
      const res = await fetch(`${API}/api/v1/admin/library/template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Template download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "role-library-template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const data = preview;
  const hasProblem = (r) =>
    r.errors.length || r.actions.some((a) => a.suggestion);
  const rows = data
    ? data.rows.filter((r) => !onlyProblems || hasProblem(r))
    : [];
  const createdTotal = data
    ? Object.values(data.summary.create).reduce((a, b) => a + b, 0)
    : 0;

  /* Which column an error belongs to, by the kind name it mentions. */
  const errorsFor = (r, col) => {
    if (!r.errors.length) return [];
    return r.errors.filter((e) => {
      const lower = e.toLowerCase();
      if (col.kind)
        return (
          lower.startsWith(col.kind) ||
          lower.startsWith(LIBRARY[col.kind].label.toLowerCase())
        );
      return false;
    });
  };
  const unplacedErrors = (r) =>
    r.errors.filter((e) => !COLUMNS.some((c) => errorsFor(r, c).includes(e)));

  /* A single-value cell: the value, then its verdict / suggestion. */
  const renderSingle = (r, c) => {
    const value = r.cells?.[c.key] || "";
    const v =
      c.key === "aliases"
        ? r.actions.find(
            (a) =>
              a.kind === "role" &&
              a.action === "update" &&
              a.field === "aliases",
          ) || null
        : c.kind
        ? r.actions.find((a) => a.kind === c.kind) || null
        : null;
    const resKey = `${r.row}:${c.kind}`;
    const chosen = !!resolutions[resKey];
    return (
      <>
        <div className={value ? "text-foreground" : "text-muted-foreground"}>
          {value || "—"}
        </div>
        <Verdict v={v} value={value} chosen={chosen} />
        {v?.suggestion ? (
          <label className="mt-1 flex items-start gap-1.5 text-xs text-amber-800">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={chosen}
              disabled={isLoading}
              onChange={() => toggleResolution(resKey, v.suggestion.id)}
            />
            <span>
              Similar to existing "{v.suggestion.name}". Use it instead?
            </span>
          </label>
        ) : null}
      </>
    );
  };

  /* A comma-list cell: one rounded chip per value, verdict under each. */
  const renderList = (r, c) => {
    const values = r.values?.[c.key] || [];
    if (!values.length) return <div className="text-muted-foreground">—</div>;
    return (
      <div className="flex flex-col gap-1.5">
        {values.map((value, index) => {
          /* Prefer the link/create/generic verdict over a plain reuse for the same value. */
          const mine = r.actions.filter(
            (a) => a.kind === c.kind && a.index === index,
          );
          const v = mine.find((a) => a.action !== "reuse") || mine[0] || null;
          const resKey = `${r.row}:${c.kind}:${index}`;
          const chosen = !!resolutions[resKey];
          const tone =
            v?.action === "create"
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : v?.action === "generic" || chosen
              ? "border-blue-200 bg-blue-50 text-blue-800"
              : "border-border bg-surface text-foreground";
          return (
            <div key={index}>
              <span
                className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium ${tone}`}>
                {value}
              </span>
              <Verdict v={v} value={value} chosen={chosen} />
              {v?.suggestion ? (
                <label className="mt-1 flex items-start gap-1.5 text-xs text-amber-800">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={chosen}
                    disabled={isLoading}
                    onChange={() => toggleResolution(resKey, v.suggestion.id)}
                  />
                  <span>
                    Similar to existing "{v.suggestion.name}". Use it instead?
                  </span>
                </label>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <ControlledModal
      open={open}
      closeModal={() => !isLoading && onClose?.()}
      closeOnDocumentClick={!isLoading}
      contentStyle={{
        width: "min(1100px, 97vw)",
        borderRadius: "12px",
        padding: 0,
        border: "none",
        background: "transparent",
      }}>
      <div className="flex max-h-[92vh] flex-col overflow-hidden rounded-2xl bg-card">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-bold text-brand-navy">Bulk upload</h2>
          <button
            type="button"
            onClick={() => onClose?.()}
            className="text-sm text-muted-foreground hover:text-foreground"
            disabled={isLoading}>
            Close
          </button>
        </div>

        <div className="flex flex-col gap-4 overflow-y-auto px-6 py-5">
          <p className="text-sm text-muted-foreground">
            Fill in only what you are adding and the level directly above it: a
            skill needs its role, a tool needs its specialty, a specialty needs
            its role, a role needs its category. Anything higher is looked up
            for you, so a sheet of skills can be just the role and skill
            columns. The level above is only required when it is new itself.
            description belongs to the category on its row. Tools and skills can
            be several per cell, separated by commas. A row with only skills
            makes them generic (no role). Names are matched ignoring case,
            spaces and hyphens, and saved in title case. Up to 2,000 rows.{" "}
            <button
              type="button"
              onClick={downloadTemplate}
              className="font-semibold text-brand-navy underline">
              Download the template
            </button>
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={inputRef}
              id="bulk-file"
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={onPick}
              disabled={isLoading}
              className="text-sm file:mr-3 file:rounded-md file:border file:border-border file:bg-background file:px-3 file:py-1.5 file:text-sm file:font-medium"
            />
            {isLoading && !preview ? (
              <span className="text-sm text-muted-foreground">
                Checking the sheet…
              </span>
            ) : null}
          </div>

          {job && job.status !== "done" && job.status !== "failed" ? (
            <div
              role="status"
              aria-live="polite"
              className="rounded-md border border-brand-navy/20 bg-surface px-3 py-2 text-sm text-brand-navy">
              <span className="font-semibold">
                Importing{job.stage ? ` ${job.stage}` : ""}…
              </span>{" "}
              {job.written_rows ? (
                <span>{job.written_rows.toLocaleString()} written so far.</span>
              ) : (
                <span>Working through the sheet.</span>
              )}{" "}
              <span className="text-muted-foreground">
                This runs on the server — you can close this and come back.
              </span>
            </div>
          ) : null}

          {error ? (
            <p
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          ) : null}

          {data ? (
            <>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-border bg-surface px-4 py-3 text-sm">
                <span className="font-semibold text-brand-navy">
                  Preview · {data.fileName}
                </span>
                <span>{data.summary.rows} rows</span>
                <span className="text-emerald-700">
                  {data.summary.valid} ok
                </span>
                {data.summary.errors ? (
                  <span className="text-red-700">
                    {data.summary.errors} with errors
                  </span>
                ) : null}
                {data.summary.suggestions ? (
                  <span className="text-amber-700">
                    {data.summary.suggestions} possible duplicate
                    {data.summary.suggestions === 1 ? "" : "s"}
                  </span>
                ) : null}
                <span className="text-muted-foreground">|</span>
                <span>
                  {createdTotal} new (
                  {KINDS.filter((k) => data.summary.create[k])
                    .map(
                      (k) =>
                        `${data.summary.create[k]} ${LIBRARY[
                          k
                        ].plural.toLowerCase()}`,
                    )
                    .join(", ") || "none"}
                  )
                </span>
                {/* <span>{data.summary.reuse} reused</span> */}
                <span>{data.summary.link} links</span>
                {data.summary.assign ? (
                  <span>{data.summary.assign} roles assigned a category</span>
                ) : null}
                {data.summary.update ? (
                  <span>{data.summary.update} descriptions updated</span>
                ) : null}
                {data.summary.generic ? (
                  <span>{data.summary.generic} skills marked generic</span>
                ) : null}
              </div>
              {data.warnings?.length ? (
                <ul className="list-disc pl-5 text-xs text-amber-700">
                  {data.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              ) : null}
              {data.summary.errors || data.summary.suggestions ? (
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={onlyProblems}
                    onChange={(e) => setOnlyProblems(e.target.checked)}
                  />{" "}
                  Show only rows that need attention
                </label>
              ) : null}

              <div
                className={`max-h-[48vh] overflow-auto rounded-md border border-border ${
                  isLoading ? "opacity-60" : ""
                }`}>
                <table className="min-w-full border-collapse text-left text-sm">
                  <thead className="sticky top-0 z-10 bg-surface text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="border-b border-border px-2 py-2 font-mono">
                        #
                      </th>
                      {COLUMNS.map((c) => (
                        <th
                          key={c.key}
                          className="border-b border-border px-3 py-2">
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const extra = unplacedErrors(r);
                      return (
                        <React.Fragment key={r.row}>
                          <tr
                            className={`border-t border-border align-top ${
                              r.errors.length ? "bg-red-50/70" : ""
                            }`}>
                            <td className="px-2 py-2 font-mono text-xs text-muted-foreground">
                              {r.row}
                            </td>
                            {COLUMNS.map((c) => {
                              const errs = errorsFor(r, c);
                              return (
                                <td key={c.key} className="px-3 py-2">
                                  {c.list
                                    ? renderList(r, c)
                                    : renderSingle(r, c)}
                                  {errs.map((e, i) => (
                                    <div
                                      key={i}
                                      className="mt-1 text-xs text-red-700">
                                      {e}
                                    </div>
                                  ))}
                                </td>
                              );
                            })}
                          </tr>
                          {extra.length ? (
                            <tr className="bg-red-50/70">
                              <td />
                              <td
                                colSpan={COLUMNS.length}
                                className="px-3 pb-2 text-xs text-red-700">
                                {extra.join(" · ")}
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-border px-6 py-4">
          <span className="text-xs text-muted-foreground">
            {job && job.status !== "failed"
              ? "The import is running on the server. Closing this window will not stop it."
              : preview
              ? preview.summary.errors
                ? "Rows with errors are skipped; everything else imports. Nothing is written until you click Import."
                : "Nothing is written until you click Import."
              : ""}
          </span>
          <Button
            type="button"
            onClick={doImport}
            disabled={
              !preview ||
              preview.summary.valid === 0 ||
              isLoading ||
              (!!job && job.status !== "failed")
            }
            className="bg-brand-navy text-white hover:bg-brand-navy/90">
            {job && job.status !== "failed"
              ? "Importing…"
              : isLoading && preview
              ? "Working…"
              : preview
              ? `Import ${preview.summary.valid} row${
                  preview.summary.valid === 1 ? "" : "s"
                }`
              : "Import"}
          </Button>
        </div>
      </div>
    </ControlledModal>
  );
}
